import { AccountsAccountRow, AccountsCell, FilterGroup } from '../components/accounts-overview/accounts-overview.types';
import { BALANCE_SHEET_POSITIONS } from '../constants/account-positions.constant';
import { IProjectAccount } from '../models/project-account.model';
import {
    AccountsPresentation,
    PresenterInput,
    displaySignOf,
    inDisplaySign,
    matchesAccountSearch,
    statementOf,
    statusOf,
    toMinor,
    toUnits,
    zeroAccountsChipOf,
} from './accounts-presentation';
import { PeriodPick, displayValuesOf, evaluateStatement } from './evaluate-statement';
import { flatStatementLayout, statementRowsOf, withUnknownPositions } from './statement-rows';

const NOTHING = 0;
const PERCENT = 100;

/**
 * The BALANCE-SHEET screen: reconcile the documents of each account.
 *
 * What makes it its own presenter rather than a flag on a shared one: the status column and its chips
 * only mean something here. They report whether the figure in a document matches the balance — a
 * question the income statement does not ask. Three columns, one filter group, one axis.
 *
 * Signs are flipped for liabilities and equity: aggregation computes debits minus credits, so those
 * arrive negative and a balance sheet shows them positive.
 */

/** The chips, in display order. */
export const BALANCE_SHEET_FILTERS = ['all', 'open', 'marked', 'reconciled', 'difference'] as const;

export type BalanceSheetFilterId = (typeof BALANCE_SHEET_FILTERS)[number];

/** Texts the page passes in, so no year and no translation is invented in here. */
export interface BalanceSheetLabels {
    readonly currentColumn: string;
    readonly priorColumn: string;
    readonly deltaColumn: string;
    readonly chip: (id: BalanceSheetFilterId, count: number) => string;
    /** The chip that shows accounts with no figures. Its own label, because it is its own axis. */
    readonly zeroAccountsChip: string;
    /**
     * Why the zero-accounts chip cannot be switched on yet; `null` or absent when it can. A chip that is
     * already on stays usable, so the user can always switch it off again.
     */
    readonly zeroAccountsLockedReason?: string | null;
    /** A block's section row, «Aktiven in CHF». Absent: the heading as it is. */
    readonly section?: (heading: string) => string;
}

function matchesFilter(account: IProjectAccount, filter: string): boolean {
    switch (filter) {
        case 'open':
            // REV-22005 — a hand-entered document value displays OPEN (see `statusOf`), so the chip
            // that counts open accounts has to find it too.
            return account.reconciliation === 'NOT_ATTEMPTED' || account.reconciliation === 'MACHINE_DISCARDED';
        case 'difference':
            return account.reconciliation === 'DIFFERENCE';
        case 'reconciled':
            return account.reconciliation === 'MATCHED';
        case 'marked':
            return account.flag !== null;
        default:
            return true;
    }
}

/**
 * A balance in displayed units, with the sign the screens show (`displaySignOf`): liabilities and
 * equity positive, income-statement lines as their effect on the result.
 *
 * Exported so the single-account detail page can compute the SAME number for its head that this
 * presenter computed for the row one click earlier (REV-21869 #163) — a local re-derivation is how
 * a passive-side account ends up showing its head and its row with opposite signs.
 */
export function amountOf(value: number, position: string | null, accountNumber?: string): number {
    return inDisplaySign(toUnits(value), displaySignOf(position, accountNumber));
}

/**
 * REV-22005 — the inverse of {@link amountOf}: a number as the balance sheet SHOWS it (and a person
 * types it) back to stored minor units in the LEDGER sign (debit − credit).
 *
 * The hand-entered document value is stored in the sign the machine stores its own reading in, so
 * the backend's `difference = document − ERP` means the same thing whoever read the statement. The
 * person, though, types what they see next to the ERP figure — which on a passive account is the
 * sign-flipped one. Without this the first liability account a trustee corrects would store the
 * opposite of what they typed.
 */
export function fromDisplayedAmount(displayed: number, position: string | null, accountNumber?: string): number {
    return inDisplaySign(toMinor(displayed), displaySignOf(position, accountNumber));
}

/** The accounts of THIS statement. Everything below counts and filters over these, never over all. */
function accountsOfStatement(accounts: readonly IProjectAccount[]): IProjectAccount[] {
    return accounts.filter((account) => statementOf(account) === 'BALANCE');
}

/**
 * `withPriorYear` is false while no prior-year journal is stored. The `Vorjahr` and `Δ %` columns then
 * go away, as the income statement's comparison columns do: an empty column would read as "nothing
 * was posted last year", which is a claim about the year, not about what we have loaded.
 *
 * `withReview` is false where the review flag is hidden: the `Markiert` chip goes with it, or it would
 * filter by a mark nobody can see.
 */
export function presentBalanceSheet(
    input: PresenterInput,
    labels: BalanceSheetLabels,
    withZeroAccounts = false,
    withPriorYear = true,
    withReview = true,
): AccountsPresentation {
    const all = accountsOfStatement(input.accounts);
    // The search narrows the statement BEFORE the chips count it, so `Offen (n)` answers for the rows
    // the search left — the same way the documents hub recounts its pills over a term (REV-22006).
    const mine = all.filter((account) => matchesAccountSearch(account, input.searchTerm));

    const periods: PeriodPick[] = [
        (account) => account.saldoInBaseCurrency.amount,
        ...(withPriorYear ? [(account: IProjectAccount) => account.priorYearSaldo?.amount ?? null] : []),
    ];
    // Without a prior year there is nothing to compare: one figure per row.
    const cellsOf = (values: readonly (number | null)[]): AccountsCell[] =>
        withPriorYear
            ? [{ value: values[0] ?? null }, { value: values[1] ?? null }, { value: changeOf(values[0], values[1]) }]
            : [{ value: values[0] ?? null }];

    // Over ALL accounts of the statement: the search and the chips hide rows, never part of a sum.
    const layout = withUnknownPositions(
        input.layout ?? flatStatementLayout('BALANCE_SHEET', BALANCE_SHEET_POSITIONS),
        all,
    );

    const rows = statementRowsOf({
        layout,
        accounts: all,
        values: evaluateStatement(layout, all, periods),
        periods,
        isVisible: (account) =>
            matchesAccountSearch(account, input.searchTerm) && matchesFilter(account, input.activeFilter),
        collapsed: input.collapsed,
        cellsOf,
        accountRowOf: (account): AccountsAccountRow => ({
            kind: 'account',
            id: account.accountId,
            number: account.accountNumber,
            label: account.name,
            status: statusOf(account.reconciliation),
            marked: account.flag !== null,
            values: cellsOf(displayValuesOf(account, periods)),
        }),
        statusOf: (account) => statusOf(account.reconciliation),
        labelOf: input.labelOf,
        layoutLabelOf: input.layoutLabelOf,
        sectionLabelOf: labels.section,
    });

    return {
        columns: [
            { label: labels.currentColumn, kind: 'saldo', current: true },
            ...(withPriorYear
                ? [
                      { label: labels.priorColumn, kind: 'saldo' as const },
                      { label: labels.deltaColumn, kind: 'deltaPercent' as const },
                  ]
                : []),
        ],
        rows,
        filterGroups: filtersOf(mine, input.activeFilter, labels, withZeroAccounts, withReview),
        showStatus: true,
    };
}

/**
 * The change against the prior year in percent, in the display sign: a liability growing from 100 to
 * 120 is +20 %. Computed here, not taken from the backend's ratio. `null` without a prior year, and
 * from a prior year of zero: a change from nothing has no percentage.
 */
function changeOf(current: number | null | undefined, prior: number | null | undefined): number | null {
    if (current == null || prior == null || prior === NOTHING) {
        return null;
    }
    return ((current - prior) / Math.abs(prior)) * PERCENT;
}

/**
 * How many balance-sheet accounts the `Offen` chip counts (REV-22004).
 *
 * The workspace header's `Konten` pill shows this number, so it is computed HERE, by the very filter
 * the chip uses — a second rule in the header is how the pill read 34 over a chip that said 20.
 */
export function openBalanceSheetAccountCount(accounts: readonly IProjectAccount[], searchTerm?: string): number {
    return accountsOfStatement(accounts).filter(
        (account) => matchesAccountSearch(account, searchTerm) && matchesFilter(account, 'open'),
    ).length;
}

/**
 * The chips, counted over this statement's accounts.
 *
 * Counted from the loaded list rather than from the summary route: that one counts the ASSIGNMENT
 * progress across the whole closing, which is a different question and a different number. Two
 * numbers labelled the same way would be worse than one.
 */
function filtersOf(
    mine: readonly IProjectAccount[],
    activeFilter: string,
    labels: BalanceSheetLabels,
    withZeroAccounts: boolean,
    withReview: boolean,
): FilterGroup[] {
    const count = (filter: BalanceSheetFilterId): number =>
        mine.filter((account) => matchesFilter(account, filter)).length;

    return [
        {
            id: 'status',
            align: 'start',
            chips: BALANCE_SHEET_FILTERS.filter((id) => withReview || id !== 'marked').map((id) => ({
                id,
                label: labels.chip(id, count(id)),
                active: activeFilter === id,
            })),
        },
        {
            // Its OWN group, on the right, away from the status chips. The status chips are exclusive
            // and filter the rows on screen; this one is a switch and changes which rows are fetched.
            // One group would invite reading it as a sixth status.
            id: 'zeroAccounts',
            align: 'end',
            chips: [
                zeroAccountsChipOf(labels.zeroAccountsChip, labels.zeroAccountsLockedReason ?? null, withZeroAccounts),
            ],
        },
    ];
}

/**
 * The accounts of this statement that a TRUSTEE has to decide about — the ones the classifier tried
 * and gave up on (`AI_UNRESOLVED`).
 *
 * ⚠️ It filtered on `accountGroup === null` until 24.08.2026, which lumped two opposite situations
 * together: an account the machine is still working on and an account the machine has abandoned. Both
 * carry no position, so both looked like a to-do. The consequence was visible before any polling
 * existed — the assignment dialog offered accounts the classifier was about to resolve, and they
 * vanished from under the user on the next read.
 *
 * `PENDING` is deliberately NOT here. It belongs in a progress indicator, never in a work list: this
 * set may only shrink when a human assigns something, which is what lets the dialog walk it without
 * the ground moving.
 */
export function unassignedBalanceSheetAccounts(accounts: readonly IProjectAccount[]): IProjectAccount[] {
    return accountsOfStatement(accounts).filter((account) => account.groupClassification === 'AI_UNRESOLVED');
}
